import Papa from 'papaparse';
import downloadCSV from '@app/utils/downloadCsv';
import * as format from '@app/utils/formatters';
import { buildBroadcastDemographicsCsvFilterRows } from '@app/utils/csvFilterSummary/buildBroadcastDemographicsCsvFilterRows';
import { prependFilterSectionToCsv } from '@app/utils/csvFilterSummary/prependFilterSectionToCsv';
import {
    AGE_BRACKET_LABEL_KEYS,
    AudienceScope,
    DEMOGRAPHIC_AGE_BRACKETS,
    DemographicAgeBracket,
} from '@app/constants/demographics';

export { AGE_BRACKET_LABEL_KEYS };

// One row of the broadcast audience endpoint response: a single age band
// measured for a single audience scope, summed over the selected broadcasts.
export type BroadcastDemographicsResponseRow = {
    ageBracket: DemographicAgeBracket;
    audienceScope: AudienceScope;
    averageAudience: number;
};

// A scope is `null`, not 0, when the response carries no row for it — either
// because the band is not measured for that scope (Under 18 is reported as
// all-persons only) or because the Gender filter excluded it.
export type DemographicsBreakdownRow = {
    ageBracket: DemographicAgeBracket;
    male: number | null;
    female: number | null;
    total: number | null;
};

type BreakdownColumn = 'male' | 'female' | 'total';
export type BreakdownTotals = Record<BreakdownColumn, number | null>;

type ScopeTotals = Partial<Record<AudienceScope, number>>;

const KNOWN_SCOPES: string[] = Object.values(AudienceScope);

// Anything the wire can put in `averageAudience` that is not a finite number
// (null, absent, a string) counts as no audience rather than poisoning the
// band's cell with NaN — `format.decimal(NaN)` renders "0", so a NaN would
// read as a measured zero.
const audienceOf = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * Total comes from the measured all-persons (`P`) row, not from Male + Female.
 * The scopes are measured and rounded independently, so P and M + F differ by
 * a few viewers, and the Under 18 band has no M/F rows at all. Male + Female
 * is only a fallback for a response with no all-persons row.
 */
const totalOf = (scopes: ScopeTotals): number | null => {
    const persons = scopes[AudienceScope.PERSONS];
    if (persons !== undefined) {
        return persons;
    }

    const male = scopes[AudienceScope.MALE];
    const female = scopes[AudienceScope.FEMALE];
    if (male === undefined && female === undefined) {
        return null;
    }

    return (male ?? 0) + (female ?? 0);
};

export const aggregateDemographics = (
    rows: BroadcastDemographicsResponseRow[] = [],
): DemographicsBreakdownRow[] => {
    const byBracket = new Map<DemographicAgeBracket, ScopeTotals>();

    // A 204, an error envelope or a `null` body all arrive here as non-array
    // `data`; the tab shows an empty table instead of crashing.
    const responseRows = Array.isArray(rows) ? rows : [];

    responseRows.forEach(({ ageBracket, audienceScope, averageAudience }) => {
        if (!DEMOGRAPHIC_AGE_BRACKETS.includes(ageBracket)) {
            return;
        }
        // An unrecognised scope code must not create a band whose every cell
        // is a dash.
        if (!KNOWN_SCOPES.includes(audienceScope)) {
            return;
        }
        const scopes = byBracket.get(ageBracket) ?? {};
        scopes[audienceScope] =
            (scopes[audienceScope] ?? 0) + audienceOf(averageAudience);
        byBracket.set(ageBracket, scopes);
    });

    // The endpoint orders rows alphabetically by band code, which puts
    // UNDER_18 last; display order comes from the canonical bracket list.
    return DEMOGRAPHIC_AGE_BRACKETS.filter(bracket =>
        byBracket.has(bracket),
    ).map(bracket => {
        const scopes = byBracket.get(bracket);
        return {
            ageBracket: bracket,
            male: scopes[AudienceScope.MALE] ?? null,
            female: scopes[AudienceScope.FEMALE] ?? null,
            total: totalOf(scopes),
        };
    });
};

// Age brackets are mutually exclusive, so the column totals are plain sums of
// the rows that carry a value. A column with no measured row stays empty
// rather than reading as a real zero.
export const sumBreakdownColumns = (
    rows: DemographicsBreakdownRow[] = [],
): BreakdownTotals =>
    (['male', 'female', 'total'] as const).reduce(
        (acc, column) => {
            // `!= null` on purpose: a row missing the key entirely is as
            // unmeasured as an explicit null, and must not sum to NaN.
            const measured = rows.filter(row => row[column] != null);
            acc[column] = measured.length
                ? measured.reduce((sum, row) => sum + row[column], 0)
                : null;
            return acc;
        },
        { male: null, female: null, total: null } as BreakdownTotals,
    );

/**
 * Shares for one line of the table (a band, or the footer totals):
 *
 * - `total`: the line's all-persons audience over the grand total, i.e. the
 *   share of everyone measured that falls in this band.
 * - `male` / `female`: that gender over the line's gendered audience
 *   (Male + Female), i.e. the gender split of the band. It is deliberately
 *   not over the all-persons figure, because Under 18 is measured as persons
 *   only and would otherwise leave the footer's genders short of 100%.
 * - `maleOfTotal` / `femaleOfTotal`: that gender over the grand total.
 *
 * Every share is `null` when either side is unmeasured or the denominator is
 * zero, so an unmeasured cell renders as a dash rather than 0% or NaN%.
 */
export type DemographicsShares = {
    total: number | null;
    male: number | null;
    maleOfTotal: number | null;
    female: number | null;
    femaleOfTotal: number | null;
};

export const shareOf = (
    part: number | null | undefined,
    whole: number | null | undefined,
): number | null =>
    part == null || whole == null || !whole ? null : part / whole;

export const getBreakdownShares = (
    line: Partial<BreakdownTotals>,
    grandTotal: number | null | undefined,
): DemographicsShares => {
    const { male = null, female = null, total = null } = line ?? {};
    const gendered = male == null || female == null ? null : male + female;

    return {
        total: shareOf(total, grandTotal),
        male: shareOf(male, gendered),
        maleOfTotal: shareOf(male, grandTotal),
        female: shareOf(female, gendered),
        femaleOfTotal: shareOf(female, grandTotal),
    };
};

export const NO_VALUE = '—';

export const formatAudience = (value: number | null | undefined) =>
    value === null || value === undefined ? NO_VALUE : format.decimal(value);

const SHARE_FORMAT = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

export const formatShare = (value: number | null | undefined) =>
    value == null ? NO_VALUE : format.percentage(value, SHARE_FORMAT);

/**
 * "52.7% (15.8%)": gender split of the band, then the share of the grand
 * total in parentheses. With the Gender filter hiding the other gender the
 * split is meaningless (it would always be 100%), so it shows as a dash while
 * the share of the grand total stays: "— (15.8%)". A gender with no audience
 * at all is a single dash.
 */
export const formatGenderShare = (
    withinGendered: number | null | undefined,
    ofTotal: number | null | undefined,
) =>
    ofTotal == null
        ? NO_VALUE
        : `${formatShare(withinGendered)} (${formatShare(ofTotal)})`;

// TODO: the spec asks to wire this to the existing broadcast export
// mechanism. This client-side export (Papa + downloadCSV, same as
// viewershipUtils.onExportToCsv) matches the rest of the page for now.
export const onExportDemographicsToCsv = (
    rows: DemographicsBreakdownRow[],
    csvDelimiter: string,
    t: (key: string, options?: any) => string,
    filters?: any,
) => {
    const column = (key: string) =>
        t(`measurement:broadcastExposure.demographics.columns.${key}`);
    const ageBracketHeader = column('ageBracket');
    const totalHeader = column('total');
    const totalShareHeader = column('totalShare');
    const maleHeader = column('male');
    const maleShareHeader = column('maleShare');
    const maleShareOfTotalHeader = column('maleShareOfTotal');
    const femaleHeader = column('female');
    const femaleShareHeader = column('femaleShare');
    const femaleShareOfTotalHeader = column('femaleShareOfTotal');

    // Unmeasured cells are exported empty so a spreadsheet does not read them
    // as zero audience.
    const cell = (value: number | null) => (value === null ? '' : value);
    const shareCell = (value: number | null) =>
        value === null ? '' : formatShare(value);

    const totals = sumBreakdownColumns(rows);

    const line = (
        label: string,
        values: BreakdownTotals,
        shares: DemographicsShares,
    ) => ({
        [ageBracketHeader]: label,
        [totalHeader]: cell(values.total),
        [totalShareHeader]: shareCell(shares.total),
        [maleHeader]: cell(values.male),
        [maleShareHeader]: shareCell(shares.male),
        [maleShareOfTotalHeader]: shareCell(shares.maleOfTotal),
        [femaleHeader]: cell(values.female),
        [femaleShareHeader]: shareCell(shares.female),
        [femaleShareOfTotalHeader]: shareCell(shares.femaleOfTotal),
    });

    const tableData = rows.map(row =>
        line(
            t(AGE_BRACKET_LABEL_KEYS[row.ageBracket]),
            row,
            getBreakdownShares(row, totals.total),
        ),
    );

    // As in the table footer, the totals line carries the gender split only;
    // its share of the grand total is left empty.
    const totalShares = getBreakdownShares(totals, totals.total);
    tableData.push(
        line(t('measurement:broadcastExposure.table.total'), totals, {
            ...totalShares,
            maleOfTotal: null,
            femaleOfTotal: null,
        }),
    );

    const csv = Papa.unparse(tableData, { delimiter: csvDelimiter });
    const filterRows = [
        [t('measurement:dataExplorer.csvExport.activeFiltersLabel'), ''],
        ...buildBroadcastDemographicsCsvFilterRows({ filters, t }),
    ];
    const csvWithFilters = prependFilterSectionToCsv(
        csv,
        filterRows,
        csvDelimiter,
    );
    // `downloadCSV` appends the `.csv` extension itself.
    downloadCSV(csvWithFilters, 'broadcast-demographics', false);
};
